import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, Play, Eye, Heart, MessageSquare } from 'lucide-react';

interface Video {
  id: string;
  title: string;
  thumbnail: string;
  category?: string;
  is_premium?: boolean;
  views_count?: number;
  likes_count?: number;
  comments_count?: number;
}

interface TopRowProps {
  title: string;
  videos: Video[];
  onSelectVideo: (id: string) => void;
  onViewAll?: (categoryTitle: string, videosList: Video[]) => void;
}

const formatCount = (count?: number) => {
  if (count === undefined || count === null || isNaN(count)) return '0';
  const num = Math.floor(count);
  if (num < 0) return '0';
  if (num >= 1000000000) return (num / 1000000000).toFixed(1).replace('.', ',') + 'B';
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace('.', ',') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace('.', ',') + ' mil';
  return num.toString();
};

export default function TopRow({ title, videos, onSelectVideo, onViewAll }: TopRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [hoveredVideoId, setHoveredVideoId] = useState<string | null>(null);

  const handleScroll = (direction: 'left' | 'right') => {
    if (rowRef.current) {
      const { scrollLeft, clientWidth } = rowRef.current;
      const scrollAmount = clientWidth * 0.75;
      rowRef.current.scrollTo({
        left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!videos || videos.length === 0) return null;

  // Pega no máximo 10 itens para o Top 10
  const limitedVideos = videos.slice(0, 10);

  return (
    <div className="top-row-container" style={{ marginBottom: '35px', width: '100%', boxSizing: 'border-box' }}>
      
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        .top-row-container { padding-left: 40px; padding-right: 40px; }
        .top-video-card { min-width: 160px; width: 160px; }
        .scroll-btn { display: flex; }
        .title-text { font-size: 13px; margin: 8px 0 4px 0; }

        @media (max-width: 768px) {
          .top-row-container { padding-left: 15px !important; padding-right: 15px !important; margin-bottom: 25px !important; }
          .top-video-card { min-width: 120px !important; width: 120px !important; }
          .scroll-btn { display: none !important; }
          .title-text { font-size: 11px !important; margin: 6px 0 2px 0 !important; }
        }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ fontSize: 'clamp(16px, 4vw, 18px)', fontWeight: '800', color: '#fff', margin: 0, letterSpacing: '0.5px' }}>
          {title}
        </h3>
        {onViewAll && (
          <span 
            onClick={() => onViewAll(title, videos)}
            style={{ fontSize: '13px', color: '#E50914', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
            Ver todos <ArrowRight size={14} />
          </span>
        )}
      </div>

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        
        <button 
          className="scroll-btn"
          onClick={() => handleScroll('left')}
          style={{ 
            position: 'absolute', left: '-20px', zIndex: 10, background: 'rgba(0,0,0,0.75)', 
            border: '1px solid rgba(255,255,255,0.15)', color: '#fff', width: '35px', height: '60px', 
            cursor: 'pointer', borderRadius: '4px', alignItems: 'center', justifyContent: 'center'
          }}>
          <ChevronLeft size={22} />
        </button>

        <div 
          ref={rowRef}
          className="no-scrollbar"
          style={{ 
            display: 'flex', gap: '15px', overflowX: 'auto', 
            paddingBottom: '10px', paddingTop: '5px', width: '100%', scrollBehavior: 'smooth' 
          }}>
          {limitedVideos.map((video, index) => {
            const isHovered = hoveredVideoId === video.id;
            const rankNumber = index + 1; // 1 ao 10

            return (
              <div 
                key={video.id} 
                className="top-video-card"
                onClick={() => onSelectVideo(video.id)}
                onMouseEnter={() => setHoveredVideoId(video.id)}
                onMouseLeave={() => setHoveredVideoId(null)}
                style={{ 
                  cursor: 'pointer', 
                  flexShrink: 0, 
                  transition: 'transform 0.3s cubic-bezier(0.5, 0, 0.1, 1)',
                  transform: isHovered ? 'scale(1.04) translateY(-4px)' : 'scale(1)',
                  zIndex: isHovered ? 20 : 1,
                  position: 'relative'
                }}>
                
                {/* 📌 FORMATO VERTICAL DE PÔSTER (2:3) estilo Amazon */}
                <div style={{ 
                  width: '100%', 
                  aspectRatio: '2 / 3', 
                  borderRadius: '8px', 
                  overflow: 'hidden', 
                  backgroundColor: '#141414', 
                  position: 'relative', 
                  border: '1px solid rgba(255,255,255,0.1)', 
                  boxShadow: '0 6px 16px rgba(0,0,0,0.6)' 
                }}>
                  
                  <img 
                    src={video.thumbnail || 'https://via.placeholder.com/200x300?text=Sem+Capa'} 
                    alt={video.title} 
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />

                  {/* 🔢 NÚMERO DO RANKING (1 ao 10) ESTILO NETFLIX/AMAZON NO CANTO */}
                  <div style={{
                    position: 'absolute',
                    bottom: '4px',
                    left: '4px',
                    color: '#fff',
                    fontSize: '48px',
                    fontWeight: '900',
                    fontFamily: 'Impact, sans-serif',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.9), -2px -2px 4px rgba(0,0,0,0.9)',
                    lineHeight: '1',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }}>
                    {rankNumber}
                  </div>

                  {isHovered && (
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <div style={{ backgroundColor: '#E50914', borderRadius: '50%', padding: '10px', color: '#fff', display: 'flex' }}>
                        <Play size={18} fill="#fff" />
                      </div>
                    </div>
                  )}

                  {video.is_premium && (
                    <span style={{ position: 'absolute', top: '6px', right: '6px', backgroundColor: '#E50914', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '3px', fontWeight: 'bold' }}>
                      VIP
                    </span>
                  )}
                </div>
                
                {/* Título */}
                <div>
                  <h4 className="title-text" style={{ 
                    fontWeight: '600', 
                    color: isHovered ? '#fff' : '#ccc', 
                    display: '-webkit-box', 
                    WebkitLineClamp: 2, 
                    WebkitBoxOrient: 'vertical', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    lineHeight: '1.2' 
                  }}>
                    {video.title}
                  </h4>
                  
                  {/* Métricas */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888', fontSize: '10px', marginTop: '4px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <Eye size={11} color="#aaa" /> {formatCount(video.views_count ?? 0)}
                    </span>
                    <span style={{ display: 'flex', alignItems: '2px', gap: '2px' }}>
                      <Heart size={11} color="#E50914" /> {formatCount(video.likes_count ?? 0)}
                    </span>
                  </div>
                </div>

              </div>
            );
          })}
        </div>

        <button 
          className="scroll-btn"
          onClick={() => handleScroll('right')}
          style={{ 
            position: 'absolute', right: '-20px', zIndex: 10, background: 'rgba(0,0,0,0.75)', 
            border: '1px solid rgba(255,255,255,0.15)', color: '#fff', width: '35px', height: '60px', 
            cursor: 'pointer', borderRadius: '4px', alignItems: 'center', justifyContent: 'center'
          }}>
          <ChevronRight size={22} />
        </button>

      </div>
    </div>
  );
}